import axiosInstance from '../axios';
import { CommonResponse } from '../interfaces/categories';
import { CardsList } from '../interfaces/cards';
import { CardService } from './cardService';

const API_URL = process.env.NEXT_PUBLIC_API_URL || "https://api.lovingecards.com/api/v1";

export interface FavoriteCardsResponse {
  response_status: boolean;
  response_message: string;
  response_data: CardsList[];
}

export const FavoriteService = {

  getUserFavoriteCardIds: async (): Promise<string[]> => {
    // Avoid calling protected endpoint during SSR where auth token is not in localStorage/window
    if (typeof window === "undefined") {
      return [];
    }

    try {
      const response = await axiosInstance.get(`${API_URL}/group-cards/favorites/`);
      if (response.data?.response_status && Array.isArray(response.data?.response_data)) {
        return response.data.response_data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching favorite card IDs:', error);
      return [];
    }
  },

  /**
   * Get full list of favorite cards
   */
  getUserFavoriteCards: async (): Promise<FavoriteCardsResponse> => {
    if (typeof window === "undefined") {
      return {
        response_status: false,
        response_message: "Cannot fetch favorites during SSR",
        response_data: []
      };
    }

    try {
      // detailed=true returns full card objects in one request instead of IDs
      // that would each need a separate /cards/<id>/ call
      const response = await axiosInstance.get(`${API_URL}/group-cards/favorites/`, {
        params: { detailed: true }
      });
      if (!response.data?.response_status) {
        return {
          response_status: false,
          response_message: response.data?.response_message || 'Failed to load favorite cards',
          response_data: []
        };
      }

      const rawData = response.data?.response_data;
      if (!Array.isArray(rawData)) {
        return {
          response_status: true,
          response_message: 'Success',
          response_data: []
        };
      }

      const cards: CardsList[] = [];
      const idsToFetch: string[] = [];

      for (const item of rawData) {
        if (!item) continue;
        if (typeof item === 'string') {
          idsToFetch.push(item);
        } else if (item.card && typeof item.card === 'object') {
          cards.push({
            id: item.card.id || item.id,
            name: item.card.name || item.card.card_title || 'Greeting Card',
            card_image: item.card.card_image || '',
            category: typeof item.card.category === 'object' ? (item.card.category?.category_name || 'General') : (item.card.category || 'General'),
            is_active: item.card.is_active ?? true,
            is_staff_pick: item.card.is_staff_pick ?? false,
            position: item.card.position ?? 0,
            is_custom: item.card.is_custom,
            slug: item.card.slug || item.card.id || item.id,
          });
        } else if (item.card_image || item.name) {
          cards.push({
            id: item.id,
            name: item.name || item.card_title || 'Greeting Card',
            card_image: item.card_image || '',
            category: typeof item.category === 'object' ? (item.category?.category_name || 'General') : (item.category || 'General'),
            is_active: item.is_active ?? true,
            is_new: item.is_new ?? false,
            is_staff_pick: item.is_staff_pick ?? false,
            position: item.position ?? 0,
            is_custom: item.is_custom,
            slug: item.slug || item.id,
          });
        } else if (item.id || item.card_id) {
          idsToFetch.push(item.card_id || item.id);
        }
      }

      // If API only returned IDs, fetch full card details concurrently
      if (idsToFetch.length > 0) {
        const detailResults = await Promise.allSettled(
          idsToFetch.map(id => CardService.getCardDetail(id))
        );
        for (const res of detailResults) {
          if (res.status === 'fulfilled' && res.value?.response_status && res.value?.response_data) {
            const cardObj = res.value.response_data as CardsList;
            if (cardObj && cardObj.id) {
              cards.push(cardObj);
            }
          }
        }
      }

      return {
        response_status: true,
        response_message: 'Favorite cards loaded successfully',
        response_data: cards
      };
    } catch (error) {
      console.error('Error fetching favorite cards:', error);
      return {
        response_status: false,
        response_message: error instanceof Error ? error.message : 'Failed to load favorite cards',
        response_data: []
      };
    }
  },

  /**
   * Toggle favorite status for a given card ID
   */
  toggleFavoriteCard: async (cardId: string): Promise<CommonResponse> => {
    try {
      const response = await axiosInstance.post(`${API_URL}/group-cards/favorites/toggle/`, {
        card_id: cardId
      });
      return response.data;
    } catch (error) {
      console.error('Error toggling card favorite:', error);
      return {
        response_status: false,
        response_message: error instanceof Error ? error.message : 'Failed to toggle favorite',
        response_data: null
      };
    }
  }
};

